import { ListContextProvider, useListContext, useRefresh, useListController } from "ra-core"
import { FC, useCallback, useState } from "react"
import { OdResourcePageProps } from "@/types/resource"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Card, CardHeader, Divider, Box, IconButton, Tooltip, Button, Typography, alpha, useTheme } from "@mui/material"
import { Datagrid } from "@/components/list/datagrid"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { FunctionField, TextField } from "@/components/field"
import ReleaseView from "../ReleaseView"
import ReleaseDownloadView from "../ReleaseDownloadView"
import NotesIcon from '@mui/icons-material/Notes';
import DownloadIcon from '@mui/icons-material/Download';
import CreateOrEdit from "@/controller/CreateOrEdit"
import DateField2 from "@/components/field/DateField2"
import { ProductReleaseDto } from "@/gen/api/user"


const resource = 'release-view'
const defaultSort = {
  field: 'releaseTime',
  order: 'DESC'
}
const SecureFileRelease: FC<OdResourcePageProps> = (props) => {
  const { resource } = props;
  const listCtrl = useListController({ resource, basePath: '', filter: { }, sort: defaultSort })

  return (
    <ListContextProvider value={listCtrl}>
      <SecureFileReleaseBody />
    </ListContextProvider>
  )
}

const SecureFileReleaseBody: FC = () => {
  const listCtx = useListContext<ProductReleaseDto & RecPatch>()

  const [states, setStates] = useState({
    openEdit: false,
    openDownload: false,
    recordToEdit: null as ProductReleaseDto | null,
    recordToDownload: null as ProductReleaseDto | null,
  })

  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])

  const theme = useTheme()

  return (
    <>
      <Card>
        <CardHeader 
          title={
            <>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                SecureFile Release
                {/* <Tooltip title="Token Validation Keys will be used to validate user tokens from applications.">
                  <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                </Tooltip> */}
              </Box>
            </>
          }
        />
        <Divider />
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                '& .description': { maxWidth: 750, display: 'inline-block', wordBreak: 'break-word' },
              }}
              {...listCtx}
            >
              <DateField2 source="releaseTime" label="Release Time" sortable={false} showTime />
              <TextField source="productVersion" label="Version" sortable={false} variant="caption" component="p" color="success.main" />
              <TextField source="releaseTag" label="Release Tag" sortable={false} className="description" />
              <FunctionField 
                label="Actions" 
                sortable={false} 
                render={(d?:RecPatch) => {
                  if (!d) { return null }
                  return (
                    <>
                      <Tooltip title="View Release Note">
                        <IconButton
                          sx={{ color: 'text.primary' as any, '&:hover': { backgroundColor: 'action.hover' }, }}
                          onClick={(e) => {
                            setStates(v => ({
                              ...v,
                              recordToEdit: d as ProductReleaseDto&RecPatch,
                              openEdit: true,
                            }))
                          }}
                        >
                          <NotesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          disabled={!(d as ProductReleaseDto&RecPatch)?.windowsAvailable && !(d as ProductReleaseDto&RecPatch)?.linuxAvailable 
                            && !(d as ProductReleaseDto&RecPatch)?.macAvailable && !(d as ProductReleaseDto&RecPatch)?.macLegacyAvailable }
                          sx={{ color: 'text.primary' as any, '&:hover': { backgroundColor: 'action.hover' }, }}
                          onClick={(e) => {
                            setStates(v => ({
                              ...v,
                              recordToDownload: d as ProductReleaseDto&RecPatch,
                              openDownload: true,
                            }))
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )
                }}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      <CreateOrEdit resource={resource} basePath="" record={states.recordToEdit} >
        {states.openEdit && 
          <ReleaseView 
            open={states.openEdit} 
            setOpen={(x:boolean) => {setStates(v => ({...v, openEdit: x})); if (!x) { setTimeout(() => setStates(v => ({...v, recordToEdit:null}))) }}} 
          />
        }
      </CreateOrEdit>
      <CreateOrEdit resource={resource} basePath="" record={states.recordToDownload}>
        {states.openDownload && 
          <ReleaseDownloadView 
            open={states.openDownload} 
            setOpen={(x:boolean) => {setStates(v => ({...v, openDownload: x})); if (!x) { setTimeout(() => setStates(v => ({...v, recordToDownload:null}))) }}} 
          />
        }
      </CreateOrEdit>
    </>
  )
}

export default SecureFileRelease;


